/* =============================================================
   AUTH — SESSION STORE
   =============================================================
   The session lives in a module-level reactive singleton rather
   than behind a provide/inject, for one concrete reason: the
   router's navigation guard runs outside any component, and it
   needs to read `status` before deciding where to send a visitor.

   `initAuth()` is called once from main.ts. `authReady()` resolves
   as soon as the first `getSession()` settles, so the guard can
   await it instead of redirecting a signed-in user to sign-in
   while their session is still being restored.
   ============================================================= */

import { computed, ref, shallowRef } from 'vue'
import { authClient } from './client'
import type { Session, SignInInput } from './types'

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous'

const session = shallowRef<Session | null>(null)
const status = ref<AuthStatus>('loading')
/** Convenience alias for `session?.user`. */
const user = computed(() => session.value?.user ?? null)

let ready: Promise<void> | null = null

function apply(next: Session | null): void {
  session.value = next
  status.value = next ? 'authenticated' : 'anonymous'
}

/**
 * Restore any existing session and follow the backend from there.
 * Safe to call more than once — only the first call does the work.
 */
export function initAuth(): Promise<void> {
  if (ready) return ready

  ready = authClient.getSession().then(apply)
  // Never unsubscribed: the store lives as long as the page does.
  authClient.onAuthStateChange(apply)

  return ready
}

/** Resolves once the initial session restore has settled. */
export function authReady(): Promise<void> {
  return ready ?? initAuth()
}

/** Read the current session and the sign-in / sign-up / sign-out actions. */
export function useAuth() {
  return {
    status,
    session,
    user,

    async signIn(input: SignInInput): Promise<void> {
      apply(await authClient.signIn(input))
    },


    async signOut(): Promise<void> {
      await authClient.signOut()
      apply(null)
    },
  }
}
